import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { leaguesAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Trophy } from 'lucide-react';
import { ImageCropUpload } from '@/components/ImageCropUpload';

export const CreateLeaguePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState([]);

  
  const [formData, setFormData] = useState({
    name: '',
    sport_id: searchParams.get('sport') || '',
    city: '',
    description: '',
    rules: '',
    is_public: true,
    cover_image_url: ''
  });

  useEffect(() => {
    const fetchSports = async () => {
      try {
        const res = await sportsAPI.getAll();
        setSports(res.data);
      } catch (error) {
        console.error('Error fetching sports:', error);
      }
    };
    fetchSports();
  }, []);

  useEffect(() => {
    const fetchLeague = async () => {
      if (!isEditing) return;
      setLoading(true);
      try {
        const res = await leaguesAPI.getById(id);
        const league = res.data.league || res.data;
        setFormData({
          name: league.name || '',
          sport_id: league.sport_id || '',
          city: league.city || '',
          description: league.description || '',
          rules: league.rules || '',
          is_public: league.is_public ?? true,
          cover_image_url: league.cover_image_url || ''
        });
      } catch (error) {
        toast.error('Errore nel caricamento della lega');
      } finally {
        setLoading(false);
      }
    };

    fetchLeague();
  }, [id, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.sport_id || !formData.city) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await leaguesAPI.update(id, formData);
        toast.success('Lega aggiornata con successo!');
        navigate(`/leagues/${id}`);
      } else {
        const res = await leaguesAPI.create(formData);
        toast.success('Lega creata con successo!');
        navigate(`/leagues/${res.data.league_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto" data-testid="create-league-page">
      <Button 
        variant="ghost" 
        className="mb-6"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            {isEditing ? 'Modifica Lega' : 'Crea Nuova Lega'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Lega *</Label>
              <Input
                id="name"
                placeholder="Es: Calcetto Milano Nord"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="league-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sport">Sport *</Label>
                <Select
                  value={formData.sport_id}
                  onValueChange={(value) => setFormData({ ...formData, sport_id: value })}
                  disabled={isEditing}
                >
                  <SelectTrigger data-testid="league-sport-select">
                    <SelectValue placeholder="Seleziona sport" />
                  </SelectTrigger>
                  <SelectContent>
                    {sports.map(sport => (
                      <SelectItem key={sport.sport_id} value={sport.sport_id}>
                        {sport.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">Città *</Label>
                <Input
                  id="city"
                  placeholder="Es: Milano"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="league-city-input"
                  disabled={isEditing}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                placeholder="Descrivi la tua lega..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Copertina</Label>
              <ImageCropUpload
                value={formData.cover_image_url}
                onChange={(url) => setFormData(prev => ({ ...prev, cover_image_url: url }))}
                preset="cover"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rules">Regolamento</Label>
              <Textarea
                id="rules"
                placeholder="Regole della lega..."
                value={formData.rules}
                onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
              <div>
                <Label htmlFor="is_public" className="font-medium">Lega Pubblica</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.is_public 
                    ? 'Chiunque può vedere e unirsi alla lega'
                    : 'Solo con codice invito'}
                </p>
              </div>
              <Switch
                id="is_public"
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                data-testid="league-public-switch"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={loading}
                data-testid="submit-league-btn"
              >
                {loading ? (isEditing ? 'Salvataggio...' : 'Creazione...') : (isEditing ? 'Salva Modifiche' : 'Crea Lega')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateLeaguePage;
